import { ChevLeftIcon, CloseIcon } from "@/assets/icons";
import { ScoringOptionsEnum } from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import DatasetAccordion from "./DatasetAccordion";
import ScoringOptionSidebar from "./ScoringOptionSidebar";
import { DataWeightSettingsValue } from "./useDataWeightSettings";
import WeightSummarySection from "./WeightSummarySection";

/**
 * Compact single-column Data Weight panel (default mode when the
 * isAdvanceDataWeightOptionsEnabled feature flag is ON).
 */
export default function SimpleDataWeightPanel({
  state,
  actions,
  derived,
  onClose
}: DataWeightSettingsValue & { onClose: () => void }) {
  const { selectedScoringOption } = state;
  const {
    setSelectedScoringOption,
    setMode,
    resetAllWeights,
    handleSaveSetting
  } = actions;
  const {
    availableDatasets,
    totalAttributes,
    hasWeightValuesChanged,
    isAllAttributeWeightZero,
    isAllDatasetWeightZero
  } = derived;

  return (
    <div
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[360px] max-h-[calc(100vh-54px)]",
        "flex flex-col absolute left-[370px] top-[30px] text-xs"
      )}
    >
      <div className="flex flex-row items-center justify-between px-4 pt-4 pb-2">
        <h2 className="text-txtHeading font-bold text-[20px]">
          Setting Data Weight
        </h2>
        <button
          className="p-0 bg-transparent border-0"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      <div className="flex flex-col gap-4 divide-y-8 divide-white-smoke mt-2 overflow-y-auto custom-scrollbar">
        <div className="px-4">
          <ScoringOptionSidebar
            selectedScoringOption={selectedScoringOption}
            setSelectedScoringOption={setSelectedScoringOption}
          />
        </div>
        <DatasetAccordion
          showAdvancedColumns={false}
          state={state}
          actions={actions}
          derived={derived}
        />
      </div>
      <div className="flex flex-col bg-white p-4 rounded-b-xl">
        {/* Advanced settings only apply to the Normalization method; for Scaled
            and Raw values there is nothing extra to configure, so the entry
            point is hidden. */}
        {selectedScoringOption === ScoringOptionsEnum.NORMALIZED && (
          <div className="flex w-full items-start justify-between gap-2 mb-3">
            <div className="flex flex-col">
              <h3 className="text-txtHeading font-bold text-base">
                Advanced Settings
              </h3>
              <span className="text-xs text-txtBody">
                Select how scores are calculated and which formula to apply.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setMode("advanced")}
              className="flex shrink-0 items-center gap-1 bg-transparent border-0 text-teal-500 text-sm font-semibold"
            >
              <span>Config</span>
              <ChevLeftIcon className="w-4 h-4 rotate-180 fill-teal-500" />
            </button>
          </div>
        )}
        <WeightSummarySection
          availableDatasets={availableDatasets}
          totalAttributes={totalAttributes}
          hasWeightValuesChanged={hasWeightValuesChanged}
          selectedScoringOption={selectedScoringOption}
          isAllAttributeWeightZero={isAllAttributeWeightZero}
          isAllDatasetWeightZero={isAllDatasetWeightZero}
          resetAllWeights={resetAllWeights}
          handleSaveSetting={() => {
            if (handleSaveSetting()) onClose();
          }}
          className="w-full p-0"
        />
      </div>
    </div>
  );
}
